import { Fragment, useState } from 'react';
import Decimal from 'decimal.js';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  useGetCashSessionQuery,
  useCloseCashSessionMutation,
} from '../../../redux/api/marktApi';
import { useApiErrorToast } from '../../../hooks/useApiError';

const euro = (v) => `${new Decimal(v || 0).toFixed(2)} €`;

const toDecimal = (str) => {
  if (str === '' || str === null || str === undefined) return null;
  try {
    return new Decimal(str);
  } catch {
    return null;
  }
};

const CloseCashRegisterModal = ({ session }) => {
  const [showModal, setShowModal] = useState(false);
  const [counting, setCounting] = useState('');
  const [withdrawal, setWithdrawal] = useState('');
  // Load the live summary (expected balance / revenue / transfers) only while open.
  const { data, isFetching } = useGetCashSessionQuery(session.id, {
    skip: !showModal,
  });
  const [doClose, { isLoading }] = useCloseCashSessionMutation();
  const apiErrorToast = useApiErrorToast();
  const navigate = useNavigate();

  const summary = data?.summary;
  const expected = summary ? new Decimal(summary.expected_balance) : null;

  const countingDec = toDecimal(counting);
  const withdrawalDec = toDecimal(withdrawal);

  const difference =
    countingDec && expected ? countingDec.minus(expected) : null;
  const countMatches = difference !== null && difference.isZero();
  const withdrawalValid =
    withdrawalDec !== null &&
    withdrawalDec.gte(0) &&
    countingDec !== null &&
    withdrawalDec.lte(countingDec);
  const endBalance =
    countMatches && withdrawalValid ? countingDec.minus(withdrawalDec) : null;
  const canSubmit =
    countMatches && withdrawalValid && !isLoading && !isFetching;

  const close = () => {
    if (isLoading) return;
    setCounting('');
    setWithdrawal('');
    setShowModal(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await doClose({
        sessionId: session.id,
        payload: { sum_counting: counting, final_transfer_out: withdrawal },
      }).unwrap();
      toast.success('Kasse abgeschlossen.');
      setShowModal(false);
      navigate('/markt/cash-register/overview');
    } catch (err) {
      apiErrorToast(err, 'Kassenabschluss fehlgeschlagen');
    }
  };

  return (
    <Fragment>
      <Button
        variant='outline-danger'
        size='sm'
        onClick={() => setShowModal(true)}
      >
        <i className='fas fa-lock me-1' />
        Kasse abschließen
      </Button>
      <Modal show={showModal} onHide={close} size='lg' backdrop={'static'}>
        <Modal.Header closeButton={!isLoading}>
          <Modal.Title>
            Kassenabschluss – {session.cash_register_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isFetching || !summary ? (
            <p>Kassenübersicht wird geladen...</p>
          ) : (
            <Fragment>
              <Table size='sm' borderless className='mb-3'>
                <tbody>
                  <tr>
                    <td>Anfangsbestand</td>
                    <td className='text-end'>{euro(summary.start_balance)}</td>
                  </tr>
                  <tr>
                    <td>Tageseinnahmen</td>
                    <td className='text-end'>{euro(summary.revenue)}</td>
                  </tr>
                  <tr>
                    <td>Einzahlungen</td>
                    <td className='text-end'>{euro(summary.transfers_in)}</td>
                  </tr>
                  <tr>
                    <td>Entnahmen</td>
                    <td className='text-end'>
                      − {euro(summary.transfers_out)}
                    </td>
                  </tr>
                  <tr className='fw-bold border-top'>
                    <td>Erwarteter Kassenstand</td>
                    <td className='text-end'>
                      {euro(summary.expected_balance)}
                    </td>
                  </tr>
                </tbody>
              </Table>

              <Form.Group className='mb-3'>
                <Form.Label>Gezählter Bargeldbestand (€)</Form.Label>
                <Form.Control
                  type='number'
                  min='0'
                  step='0.01'
                  value={counting}
                  onChange={(e) => setCounting(e.target.value)}
                  autoFocus
                />
              </Form.Group>

              {difference !== null && !countMatches && (
                <div className='alert alert-warning py-2'>
                  Differenz: <strong>{euro(difference)}</strong>. Der Abschluss
                  ist erst möglich, wenn der gezählte Betrag dem erwarteten
                  Kassenstand entspricht – buche zuerst eine Einzahlung oder
                  Entnahme zur Korrektur.
                </div>
              )}

              <Form.Group className='mb-2'>
                <Form.Label>Entnahme bei Abschluss (€)</Form.Label>
                <Form.Control
                  type='number'
                  min='0'
                  step='0.01'
                  value={withdrawal}
                  onChange={(e) => setWithdrawal(e.target.value)}
                />
                <Form.Text>
                  Betrag, der zum Tagesabschluss entnommen wird. Der Rest
                  verbleibt als Wechselgeld für die nächste Sitzung.
                </Form.Text>
              </Form.Group>

              {withdrawalDec !== null &&
                countingDec !== null &&
                !withdrawalValid && (
                  <div className='alert alert-warning py-2'>
                    Entnahme darf den gezählten Betrag nicht übersteigen.
                  </div>
                )}

              {endBalance !== null && (
                <p className='fw-bold mb-0'>
                  Verbleibendes Wechselgeld: {euro(endBalance)}
                </p>
              )}
            </Fragment>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={close} disabled={isLoading}>
            abbrechen
          </Button>
          <Button variant='danger' onClick={handleSubmit} disabled={!canSubmit}>
            Kasse abschließen
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default CloseCashRegisterModal;
